import { EditNote } from "@mui/icons-material";
import DescriptionIcon from "@mui/icons-material/Description";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Box, Button, IconButton, Menu, MenuItem, Toolbar } from "@mui/material";
import type React from "react";
import { useState } from "react";
import { Link, useLocation } from "react-router";
import { NAVIGATION_HEIGHT } from "@/config/constants";
import { getSelectedContentByPath } from "@/shared/utils";

export function Navigation(): React.ReactElement {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const location = useLocation();
	const pathname = location.pathname;
	const currentSelectedContent = getSelectedContentByPath(pathname);

	const navigationItems = [
		{
			label: "利用規約",
			shortLabel: "利用規約",
			icon: <DescriptionIcon />,
			path: "/terms/glico-members",
		},
		{
			label: "お知らせ",
			shortLabel: "Info管理",
			icon: <EditNote />,
			path: "/informations",
		},
	];

	// 全てのアイテムを表示し、オーバーフローした場合のみメニューに移動
	const visibleItems = navigationItems;
	const hiddenItems: typeof navigationItems = [];

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const handleMenuItemClick = () => {
		handleMenuClose();
	};

	return (
		<Box
			sx={{
				gridArea: "navigation",
				height: NAVIGATION_HEIGHT,
				display: "flex",
				alignItems: "center",
				width: "100%",
				backgroundColor: (theme) => theme.palette.primary.main,
				position: "sticky",
				top: 0,
				zIndex: 1000,
			}}
		>
			<Toolbar sx={{ width: "100%", justifyContent: "center", px: 2 }}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-evenly",
						alignItems: "center",
						width: "100%",
						maxWidth: "1200px",
						gap: { xs: 0.5, sm: 1, md: 2 },
					}}
				>
					{visibleItems.map(({ label, shortLabel, icon, path }) => (
						<Button
							key={label}
							component={Link}
							to={path}
							sx={{
								color: "#FFFFFF",
								borderBottom:
									currentSelectedContent === label ? "4px solid hsl(12, 98.10%, 58.40%)" : "none",
								borderRadius: "8px 8px 0 0",
								fontSize: { xs: "0.7rem", sm: "0.75rem", md: "0.875rem" },
								padding: { xs: "4px 6px", sm: "6px 8px", md: "8px 12px" },
								minWidth: 0,
								flex: 1,
								maxWidth: { xs: "none", md: "300px" },
								textAlign: "center",
								"&:hover": {
									bgcolor:
										currentSelectedContent === label ? "#ed8297" : "rgba(255, 255, 255, 0.32)",
								},
								"& .MuiButton-startIcon": {
									marginRight: { xs: "2px", sm: "4px", md: "8px" },
									marginLeft: 0,
								},
							}}
						>
							{icon}
							<Box
								component="span"
								sx={{
									ml: { xs: 0.25, sm: 0.5 },
									display: { xs: "none", sm: "inline" },
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}
							>
								{label}
							</Box>
							{/* モバイルでは指定した短縮版を表示 */}
							<Box
								component="span"
								sx={{
									ml: 0.25,
									display: { xs: "inline", sm: "none" },
									fontSize: "0.6rem",
								}}
							>
								{shortLabel}
							</Box>
						</Button>
					))}

					{hiddenItems.length > 0 && (
						<div>
							<IconButton
								onClick={handleMenuOpen}
								sx={{
									color: "#FFFFFF",
									"&:hover": {
										bgcolor: "rgba(255, 255, 255, 0.32)",
									},
								}}
							>
								<MoreHorizIcon />
							</IconButton>
							<Menu
								anchorEl={anchorEl}
								open={Boolean(anchorEl)}
								onClose={handleMenuClose}
								anchorOrigin={{
									vertical: "bottom",
									horizontal: "center",
								}}
								transformOrigin={{
									vertical: "top",
									horizontal: "center",
								}}
							>
								{hiddenItems.map(({ label, icon, path }) => (
									<MenuItem
										key={label}
										component={Link}
										to={path}
										onClick={() => handleMenuItemClick()}
										sx={{
											backgroundColor: currentSelectedContent === label ? "#f0f0f0" : "transparent",
										}}
									>
										{icon}
										<Box sx={{ ml: 1 }}>{label}</Box>
									</MenuItem>
								))}
							</Menu>
						</div>
					)}
				</Box>
			</Toolbar>
		</Box>
	);
}
